import React, { useEffect } from "react";
import { createPortal } from "react-dom";

// Props: attachments, open/close logic
interface Attachment {
  id: string;
  url: string;
  name: string;
  size: number;
  public_id: string;
  resource_type: string; // "image", "video", "pdf", "raw"
}

interface ViewAttachmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachments: Attachment[];
}

const getCloudinaryDownloadUrl = (url: string, public_id: string) => {
  const urlParts = url.split("/upload/");
  if (urlParts.length === 2) {
    const ext = url.substring(url.lastIndexOf(".") + 1);
    const fileName = public_id.split("/").pop() || "file";
    // Append file extension to filename for download naming
    const fileNameWithExt = fileName.endsWith(ext)
      ? fileName
      : `${fileName}.${ext}`;
    return `${urlParts[0]}/upload/fl_attachment/${urlParts[1]}`;
  }
  return url;
};

const renderPreview = (attachment: Attachment) => {
  if (attachment.resource_type === "image") {
    return (
      <img
        src={attachment.url}
        alt={attachment.name}
        className="file-preview-image"
        style={{
          width: "100%",
          maxHeight: 180,
          objectFit: "contain",
          borderRadius: 8,
        }}
      />
    );
  }
  if (attachment.resource_type === "video") {
    return (
      <video
        controls
        style={{ width: "100%", maxHeight: 180, borderRadius: 8 }}>
        <source src={attachment.url} />
      </video>
    );
  }
  //   if (attachment.resource_type === "pdf" || attachment.name.endsWith(".pdf")) {
  //     return (
  //       <iframe
  //         src={attachment.url}
  //         title={attachment.name}
  //         style={{ width: "100%", height: 180, border: "none", borderRadius: 8 }}
  //       />
  //     );
  //   }
  // For other file types, just show an icon
  return (
    <div
      className="file-type-icon"
      style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 40,
      }}>
      <i className="ti ti-file"></i>
    </div>
  );
};

const DisableScroll: React.FC = () => {
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);
  return null;
};

const ViewAttachmentsModal: React.FC<ViewAttachmentsModalProps> = ({
  isOpen,
  onClose,
  attachments,
}) => {
  if (!isOpen) return null;

  const handleContentClick = (e: React.MouseEvent) => e.stopPropagation();
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <>
      <DisableScroll /> {/* Prevent background scroll */}
      {/* Modal Backdrop */}
      <div
        className="modal-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          padding: "20px",
        }}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachments-modal-title">
        {/* Modal Content */}
        <div
          className="modal-content"
          style={{
            background: "#fff",
            borderRadius: 12,
            maxWidth: 1000,
            width: "100%",
            maxHeight: "95vh",
            overflowY: "auto",
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            position: "relative",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={handleContentClick}
          tabIndex={-1}>
          {/* Header */}
          <div
            style={{
              padding: 24,
              borderBottom: "1px solid #e9ecef",
              background: "#f8f9fa",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <h3
              id="attachments-modal-title"
              style={{ fontWeight: 600, fontSize: "1.4rem", margin: 0 }}>
              Attachments
            </h3>
            <button
              className="btn btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          {/* Attachments Panel */}
          <div style={{ padding: 24, background: "#fff", flexGrow: 1 }}>
            {attachments.length === 0 ? (
              <div className="text-center py-4">
                <i className="ti ti-file text-muted" style={{ fontSize: 40 }} />
                <h5 className="mt-2 mb-1 text-muted">No Attachments</h5>
                <p>There are no attachments for this report.</p>
              </div>
            ) : (
              <div
                className="attachments-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 24,
                }}>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="attachment-card"
                    style={{
                      border: "1px solid #ececec",
                      borderRadius: 8,
                      padding: 16,
                      background: "#fafbfc",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}>
                    {renderPreview(attachment)}
                    <div style={{ marginTop: 12, width: "100%" }}>
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                        {attachment.name}
                      </div>
                      <div style={{ color: "#818181", fontSize: "0.9rem" }}>
                        {(attachment.size / 1024).toFixed(1)} KB
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <a
                          href={getCloudinaryDownloadUrl(
                            attachment.url,
                            attachment.public_id
                          )}
                          className="btn btn-sm btn-outline-primary"
                          download={attachment.name}
                          //   target="_blank"
                          rel="noopener noreferrer">
                          <i className="ti ti-download"></i> Download
                        </a>
                        <a
                          href={attachment.url}
                          className="btn btn-sm btn-outline-secondary"
                          target="_blank"
                          rel="noopener noreferrer">
                          <i className="ti ti-eye"></i> View
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Footer */}
          <div
            style={{
              padding: "20px 24px",
              borderTop: "1px solid #e9ecef",
              background: "#f8f9fa",
              borderRadius: "0 0 12px 12px",
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
            }}>
            <button
              type="button"
              className="btn btn-md btn-primary"
              onClick={onClose}>
              <i className="ti ti-x me-1"></i> Close
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ViewAttachmentsModal;
